import React, { useState, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { useSpring, animated } from 'react-spring';
import { useSwipeable } from 'react-swipeable';
import { colors, Box } from 'hui';
import useWindowSize from '../../hooks/useWindowSize';

const Dot = styled.div`
  background: ${props => (props.selected ? colors.primary : colors.tint3)};
  transition: 0.5s all;
  border: 1px solid ${props => (props.selected ? colors.primary : colors.tint3)};
  width: 0.5rem;
  height: 0.5rem;
  margin: 0.5rem;
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
`;

const DotContainer = styled.div`
  position: absolute;
  bottom: 0.5rem;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Dots: React.FC<{ count: number; currentIndex: number; setIndex: (idx: number) => void }> = ({
  count,
  currentIndex,
  setIndex
}) => {
  return (
    <DotContainer>
      {Array.from({ length: count }).map((_, i) => (
        <Dot key={i} selected={i === currentIndex} onClick={() => setIndex(i)} />
      ))}
    </DotContainer>
  );
};

const CarouselWrapper = styled.div`
  overflow: hidden;
  position: relative;
`;

const ImageCarousel: React.SFC<{
  autoplayMs?: number;
  showDots?: boolean;
  infinite?: boolean;
  initialIndex?: number;
}> = ({ children, autoplayMs = 3000, showDots = true, infinite, initialIndex = 0 }) => {
  const [currentIndex, setIndex] = useState(initialIndex);
  const [swipeDelta, setSwiping] = useState(0);
  const [props, setXTranslation] = useSpring(() => ({ x: 0 }));
  const count = React.Children.count(children);
  const [containerWidth, setContainerWidth] = useState(0);
  const [childrenWidth, setChildrenWidth] = useState(0);
  const { width: windowWidth } = useWindowSize();
  const containerRef = useRef<HTMLDivElement>();
  const childrenContainer = useRef<HTMLDivElement>();
  useLayoutEffect(() => {
    if (childrenContainer.current) {
      setChildrenWidth(childrenContainer.current?.children[0]?.getBoundingClientRect()?.width);
    }
  }, [childrenContainer.current, windowWidth]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setContainerWidth(childrenContainer.current?.getBoundingClientRect()?.width);
    }
  }, [containerRef.current, windowWidth]);

  const handlers = useSwipeable({
    onSwipedRight: () => {
      setIndex((currentIndex + 1) % count);
      setSwiping(0);
    },
    onSwipedLeft: () => {
      setIndex((currentIndex || count) - 1);
      setSwiping(0);
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  const isCarouselMode = containerWidth < childrenWidth * count;

  useLayoutEffect(() => {
    if (swipeDelta > 0) return;
    if (!isCarouselMode) {
      setIndex(0);
      setSwiping(0);
      return;
    }
    if (!containerWidth) return;
    const timeout = setTimeout(() => {
      setIndex((currentIndex + 1) % count);
      setSwiping(0);
    }, autoplayMs);
    return () => clearTimeout(timeout);
  }, [currentIndex, swipeDelta, count, autoplayMs, containerWidth, childrenWidth, windowWidth]);

  setXTranslation({
    x: (infinite ? currentIndex + count : currentIndex) * childrenWidth - swipeDelta
  });
  return (
    <CarouselWrapper
      ref={containerRef}
      onMouseDown={e => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }}
      onTouchStart={e => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }}
    >
      <animated.div
        // eslint-disable-next-line react/forbid-component-props
        style={{
          transform: props.x.interpolate(x => `translateX(-${x}px)`)
        }}
        {...handlers}
      >
        <Box flexbox ref={childrenContainer}>
          {infinite && children}
          {children}
          {infinite && children}
        </Box>
      </animated.div>
      {isCarouselMode && showDots && (
        <Dots count={count} currentIndex={currentIndex} setIndex={setIndex} />
      )}
    </CarouselWrapper>
  );
};

export default ImageCarousel;
